import { clsx } from "clsx";
import {
  AlertCircle,
  CheckCircle2,
  CircleDashed,
  Loader2,
  MinusCircle,
  Undo2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  Badge,
  Button,
  Card,
  Modal,
  Select,
  Textarea,
} from "@/components/common";
import {
  useConfirmNoCommission,
  useMonthChecklist,
  useRevokeNoCommission,
} from "@/hooks/useSettlements";
import type { MonthChecklistItem } from "@/types/settlement";

const MONTHS = [
  "Styczeń",
  "Luty",
  "Marzec",
  "Kwiecień",
  "Maj",
  "Czerwiec",
  "Lipiec",
  "Sierpień",
  "Wrzesień",
  "Październik",
  "Listopad",
  "Grudzień",
];

const MONTH_OPTIONS = MONTHS.map((label, i) => ({
  value: String(i + 1),
  label,
}));

function yearOptions(): { value: string; label: string }[] {
  const currentYear = new Date().getFullYear();
  const options = [];
  for (let y = currentYear; y >= currentYear - 3; y--) {
    options.push({ value: String(y), label: String(y) });
  }
  return options;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
  }).format(value);
}

// Missing first: those are what is left to do.
const STATUS_ORDER: Record<MonthChecklistItem["status"], number> = {
  Missing: 0,
  ConfirmedNoCommission: 1,
  HasCommissions: 2,
};

/**
 * Per-month list of every insurer: are its commissions in, or did an administrator
 * confirm there are none? Settlements of the month cannot be approved until all are done.
 * Administrators only — the API refuses everyone else.
 */
export function MonthChecklistSection() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [confirming, setConfirming] = useState<MonthChecklistItem | null>(null);
  const [note, setNote] = useState("");

  const { data, isLoading, isError, error } = useMonthChecklist(
    Number(year),
    Number(month),
  );
  const confirmMutation = useConfirmNoCommission();
  const revokeMutation = useRevokeNoCommission();

  const items = [...(data?.items ?? [])].sort(
    (a, b) =>
      STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
      a.insurerName.localeCompare(b.insurerName, "pl"),
  );
  const missingCount = items.filter((i) => i.status === "Missing").length;
  const progress =
    data && data.totalInsurers > 0
      ? Math.round((data.doneInsurers / data.totalInsurers) * 100)
      : 0;

  function openConfirm(item: MonthChecklistItem) {
    confirmMutation.reset();
    setNote("");
    setConfirming(item);
  }

  function executeConfirm() {
    if (!confirming) {
      return;
    }
    confirmMutation.mutate(
      {
        insurerId: confirming.insurerId,
        year: Number(year),
        month: Number(month),
        note: note.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success(
            `${confirming.insurerName}: potwierdzono brak prowizji`,
          );
          setConfirming(null);
        },
      },
    );
  }

  function revoke(item: MonthChecklistItem) {
    revokeMutation.mutate(
      { insurerId: item.insurerId, year: Number(year), month: Number(month) },
      {
        onSuccess: () =>
          toast.success(`${item.insurerName}: cofnięto potwierdzenie`),
        onError: (e) => toast.error((e as Error).message),
      },
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select
          size="sm"
          value={month}
          onChange={setMonth}
          options={MONTH_OPTIONS}
          aria-label="Miesiąc"
        />
        <Select
          size="sm"
          value={year}
          onChange={setYear}
          options={yearOptions()}
          aria-label="Rok"
        />
      </div>

      {isLoading ? (
        <Card>
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-faint" />
          </div>
        </Card>
      ) : isError || !data ? (
        <Card>
          <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
            <AlertCircle size={16} />
            {(error as Error)?.message ??
              "Nie udało się pobrać listy kontrolnej."}
          </div>
        </Card>
      ) : (
        <>
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-main">
                  {MONTHS[data.month - 1]} {data.year}: sprawdzone{" "}
                  {data.doneInsurers} z {data.totalInsurers} firm
                </p>
                <p className="mt-0.5 text-xs text-dim">
                  {data.isComplete
                    ? "Miesiąc kompletny — można zatwierdzać rozliczenia agentów."
                    : `Brakuje ${missingCount} ${missingCount === 1 ? "firmy" : "firm"}. Zaimportuj ich pliki albo potwierdź, że w tym miesiącu nie ma od nich prowizji. Do tego czasu zatwierdzanie rozliczeń jest zablokowane.`}
                </p>
              </div>
              <Badge variant={data.isComplete ? "success" : "warning"}>
                {data.isComplete ? "Kompletny" : "Niekompletny"}
              </Badge>
            </div>
            <div
              className="mt-3 h-2 overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className={clsx(
                  "h-full rounded-full transition-all",
                  data.isComplete ? "bg-green-500" : "bg-amber-500",
                )}
                style={{ width: `${progress}%` }}
              />
            </div>
          </Card>

          <Card padding="none">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-edge bg-subtle">
                    <th className="px-4 py-3 text-left text-xs font-medium text-dim">
                      Ubezpieczyciel
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-dim">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-dim">
                      Polisy
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-dim">
                      Prowizja
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-dim">
                      Akcje
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge-soft">
                  {items.map((item) => (
                    <ChecklistRow
                      key={item.insurerId}
                      item={item}
                      onConfirm={() => openConfirm(item)}
                      onRevoke={() => revoke(item)}
                      isRevoking={
                        revokeMutation.isPending &&
                        revokeMutation.variables?.insurerId === item.insurerId
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      <Modal
        isOpen={confirming !== null}
        onClose={() => setConfirming(null)}
        title="Potwierdź brak prowizji"
      >
        <div className="space-y-4">
          <p className="text-sm text-sub">
            Potwierdzasz, że <strong>{confirming?.insurerName}</strong> nie ma
            prowizji za {MONTHS[Number(month) - 1]?.toLowerCase()} {year}. Jeśli
            prowizje przyjdą później, wystarczy je zaimportować.
          </p>
          <Textarea
            label="Notatka (opcjonalnie)"
            placeholder="np. brak sprzedaży, raport zerowy z portalu"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={300}
            rows={2}
          />
          {confirmMutation.isError && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">
              <AlertCircle size={16} />
              {(confirmMutation.error as Error)?.message ?? "Wystąpił błąd."}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setConfirming(null)}>
              Anuluj
            </Button>
            <Button
              variant="primary"
              onClick={executeConfirm}
              isLoading={confirmMutation.isPending}
              disabled={confirmMutation.isPending}
              leftIcon={<CheckCircle2 size={16} />}
            >
              Potwierdź
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function ChecklistRow({
  item,
  onConfirm,
  onRevoke,
  isRevoking,
}: {
  item: MonthChecklistItem;
  onConfirm: () => void;
  onRevoke: () => void;
  isRevoking: boolean;
}) {
  return (
    <tr
      className={clsx(
        item.status === "Missing" && "bg-amber-50/50 dark:bg-amber-950/10",
      )}
    >
      <td className="px-4 py-3 text-sm font-medium text-main">
        {item.insurerName}
      </td>
      <td className="px-4 py-3 text-sm">
        {item.status === "HasCommissions" && (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-green-700 dark:text-green-400">
            <CheckCircle2 size={16} aria-hidden />
            {item.importCount > 0
              ? `Zaimportowano · ${item.importCount} ${item.importCount === 1 ? "plik" : "pliki"}`
              : "Dodano ręcznie"}
          </span>
        )}
        {item.status === "ConfirmedNoCommission" && (
          <span className="inline-flex flex-col">
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sub">
              <MinusCircle size={16} aria-hidden />
              Brak prowizji
            </span>
            <span className="ml-[22px] text-xs text-dim">
              potwierdzone
              {item.confirmedAt &&
                ` ${new Date(item.confirmedAt).toLocaleDateString("pl-PL")}`}
              {item.note && ` · ${item.note}`}
            </span>
          </span>
        )}
        {item.status === "Missing" && (
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-medium text-amber-700 dark:text-amber-400">
            <CircleDashed size={16} aria-hidden />
            Nie sprawdzono
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right text-sm tabular-nums text-sub">
        {item.policyCount > 0 ? item.policyCount : "—"}
        {item.manualPolicyCount > 0 && (
          <span className="block text-xs text-dim">
            w tym ręcznie: {item.manualPolicyCount}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right text-sm tabular-nums text-sub">
        {item.policyCount > 0 ? formatCurrency(item.commissionTotal) : "—"}
      </td>
      <td className="px-4 py-3 text-right">
        {item.status === "Missing" && (
          <Button
            size="sm"
            variant="outline"
            onClick={onConfirm}
            className="whitespace-nowrap"
          >
            Brak prowizji
          </Button>
        )}
        {item.status === "ConfirmedNoCommission" && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onRevoke}
            isLoading={isRevoking}
            disabled={isRevoking}
            leftIcon={<Undo2 size={14} />}
          >
            Cofnij
          </Button>
        )}
      </td>
    </tr>
  );
}
